import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  DataGridPremium,
  useGridApiRef,
  gridClasses,
} from "@mui/x-data-grid-premium";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { darken } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  selectBatchItems,
  selectBatchPagination,
  selectLoadingBatch,
  selectSelectedBatchId,
  selectLockedState,
  selectWipSaving,
  selectWipError,
} from "../../selectors/wip.selector";
import { selectUserInfo, selectUserId } from "../../selectors/login.selector";
import { createWipColumnsDynamic } from "../WIP/wipColumns";
import WIPCommentModal from "../WIP/WIPCommentModal.component";
import { bulkUpdateWipDetails } from "../../reducers/wip.reducer";
import { LicenseInfo } from "@mui/x-license";
LicenseInfo.setLicenseKey(
  "fd65fb21e6b06577b510cbbfe2cc8f3dTz0xMTMxNDIsRT0xNzkzNjYzOTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);
/**
 * WIP Batch List Component - Reuses wipTrackerDetails structure
 * Displays all WIP items with all stages in Excel-style DataGrid
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of WIP items to display (optional, defaults to Redux state)
 * @param {boolean} props.loading - Loading state (optional, defaults to Redux state)
 * @param {boolean} props.saving - Saving state (optional, defaults to Redux state)
 * @param {string} props.currentUserId - Current user ID (optional, defaults to Redux state)
 * @param {string} props.currentUserName - Current user name (optional, defaults to Redux state)
 * @param {boolean} props.readOnly - Whether the component is read-only (optional, defaults to false)
 * @param {Function} props.onBack - Callback for back button (optional)
 * @param {string} props.backPath - Path to navigate back to (optional)
 */
const WIPBatchList = ({
  items: itemsProp,
  loading: loadingProp,
  saving: savingProp,
  currentUserId: currentUserIdProp,
  currentUserName: currentUserNameProp,
  readOnly = false,
  onBack,
  backPath,
} = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  // Use props if provided, otherwise fall back to Redux selectors
  const itemsFromRedux = useSelector(selectBatchItems);
  const paginationFromRedux = useSelector(selectBatchPagination);
  const loadingFromRedux = useSelector(selectLoadingBatch);
  const savingFromRedux = useSelector((state) => state.wip.saving);
  const selectedBatchIdFromRedux = useSelector(selectSelectedBatchId);
  const lockedState = useSelector(selectLockedState);
  const userInfo = useSelector(selectUserInfo);
  const currentUserIdFromRedux = useSelector(selectUserId);

  const items = itemsProp !== undefined ? itemsProp : itemsFromRedux;
  const pagination =
    itemsProp !== undefined
      ? { total: itemsProp.length }
      : paginationFromRedux || { total: 0 };
  const loading = loadingProp !== undefined ? loadingProp : loadingFromRedux;
  const saving = savingProp !== undefined ? savingProp : savingFromRedux;
  const selectedBatchId =
    itemsProp !== undefined ? null : selectedBatchIdFromRedux; // Only needed when using Redux items
  const currentUserId =
    currentUserIdProp !== undefined
      ? currentUserIdProp
      : currentUserIdFromRedux || userInfo?.userId;
  const currentUserName =
    currentUserNameProp !== undefined
      ? currentUserNameProp
      : userInfo?.name || userInfo?.fullName;

  // Unsaved changes tracking
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });

  // Quick action states
  const [highlightReqActual, setHighlightReqActual] = useState(true);
  const [highlightActual, setHighlightActual] = useState(false);
  const [highlightPastDue, setHighlightPastDue] = useState(false);

  // Header collapse state
  const [headerExpanded, setHeaderExpanded] = useState(false);

  // Section display states
  const [wrapText, setWrapText] = useState(true);
  const [frozenColumns, setFrozenColumns] = useState([
    "vendorStyle",
    "po",
    "rootsStyle",
    "description",
  ]);

  // Filter model state for client-side filtering
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleFilterModelChange = useCallback((model) => {
    setFilterModel(model);
  }, []);

  // Bulk selection state: Map<field, Set<rowId>>
  const [selectedCells, setSelectedCells] = useState(new Map());

  // Bulk update value state: Map<field, value>
  const [bulkUpdateValues, setBulkUpdateValues] = useState(new Map());

  // Bulk update modal state
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [bulkUpdateField, setBulkUpdateField] = useState(null);
  const [bulkUpdateValue, setBulkUpdateValue] = useState("");
  const [bulkUpdateDateValue, setBulkUpdateDateValue] = useState(null);

  // PERFORMANCE OPTIMIZATION: Fixed row height (memoized)
  // Using fixed height instead of 'auto' significantly improves performance
  const rowHeight = useMemo(() => {
    // If wrapText is enabled, use a slightly larger fixed height
    // This avoids expensive row measurement calculations
    return wrapText ? 80 : 52;
  }, [wrapText]);
  const [visibleGroups, setVisibleGroups] = useState({
    mainInfo: true,
    tc: true,
    material: true,
    fabricApproval: true,
    trimApproval: true,
    sampleStatus: true,
    testing: true,
    production: true,
    inspection: true,
    logisticsDates: true,
    generalComments: true,
  });

  // Comment modal state
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentModalData, setCommentModalData] = useState({
    row: null,
    field: null,
    approvalInfo: null,
  });
  const commentModalDataRef = useRef(commentModalData);

  // Snackbar state for save notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Auto-save indicator state
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const isAutoSaveRef = useRef(false);

  // Get save error state from Redux (saving state already declared above)
  const saveErrorFromRedux = useSelector(selectWipError);
  const [previousSaving, setPreviousSaving] = useState(false);
  const [previousError, setPreviousError] = useState(null);

  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  // Ref to store handleSaveChanges to avoid circular dependency
  const handleSaveChangesRef = useRef(null);
  // Ref to store wipColumns to avoid circular dependency
  const wipColumnsRef = useRef([]);

  // Listen to save success/error state changes
  useEffect(() => {
    // Update previousSaving whenever saving changes
    const savingChanged = previousSaving !== saving;

    if (savingChanged) {
      console.log("🔄 Save state changed:", {
        previousSaving,
        saving,
        saveErrorFromRedux,
        isAutoSaveRef: isAutoSaveRef.current,
        savingChanged,
      });
    }

    // Detect when saving transitions from true to false (save completed)
    if (previousSaving && !saving) {
      // Save completed - capture ref value immediately and reset it
      const wasAutoSave = isAutoSaveRef.current;
      isAutoSaveRef.current = false; // Reset immediately
      console.log(
        "💾 Save completed. wasAutoSave:",
        wasAutoSave,
        "error:",
        !!saveErrorFromRedux
      );

      if (saveErrorFromRedux) {
        // Save failed - always show snackbar for errors (both manual and auto)
        console.log("❌ Save failed - showing error snackbar");
        setSnackbarMessage(saveErrorFromRedux || "Failed to save changes");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        // Save succeeded
        // Always update timestamp on successful save
        setLastSavedTime(new Date());

        if (wasAutoSave) {
          // Auto-save: no snackbar, just timestamp
          console.log(
            "✅ Auto-save succeeded - updating timestamp only, no snackbar"
          );
          // Explicitly ensure snackbar is closed and cleared
          setSnackbarOpen(false);
          setSnackbarMessage("");
        } else {
          // Manual save: show success snackbar AND update timestamp
          console.log("✅ Manual save succeeded - showing snackbar");
          setSnackbarMessage("Changes saved successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
      }
    }

    // Always update previousSaving to track state changes
    if (savingChanged) {
      setPreviousSaving(saving);
    }
    setPreviousError(saveErrorFromRedux);
  }, [saving, saveErrorFromRedux, previousSaving]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Color scheme for column groups (same as wipTrackerDetails)
  const columnGroupColors = {
    mainInfo: { bg: "#E3F2FD", header: "#1976D2" },
    tc: { bg: "#F3E5F5", header: "#7B1FA2" },
    material: { bg: "#FFF3E0", header: "#F57C00" },
    fabricApproval: { bg: "#E8F5E9", header: "#388E3C" },
    trimApproval: { bg: "#FCE4EC", header: "#C2185B" },
    sampleStatus: { bg: "#FFF9C4", header: "#F9A825" },
    testing: { bg: "#E0F2F1", header: "#00796B" },
    production: { bg: "#FFEBEE", header: "#D32F2F" },
    inspection: { bg: "#E8EAF6", header: "#3949AB" },
    logisticsDates: { bg: "#F1F8E9", header: "#689F38" },
    generalComments: { bg: "#EFEBE9", header: "#5D4037" },
  };

  // PERFORMANCE OPTIMIZATION: Pre-calculate column group styles
  // This avoids recalculating styles on every render
  const columnGroupStyles = useMemo(() => {
    return Object.keys(columnGroupColors).reduce((acc, groupId) => {
      acc[`& .group-header-${groupId}`] = {
        backgroundColor: `${columnGroupColors[groupId].bg} !important`,
        color: `${columnGroupColors[groupId].header} !important`,
        fontWeight: 600,
        fontSize: "12px",
      };
      return acc;
    }, {});
  }, [columnGroupColors]);

  // DEBUG: Log items and date fields
  useEffect(() => {
    if (items.length > 0) {
      const sampleItem = items[0];

      // Check date fields
      const dateFields = Object.keys(sampleItem).filter(
        (key) =>
          key.includes("(REQ)") ||
          key.includes("(ACTUAL)") ||
          key.includes("DATE")
      );
    }
  }, [items]);

  // Helper to parse field name and extract stageKey
  // Format: "stageKey: substageKey (FIELD)" for nested fields
  // Format: "stageKey_fieldName" for simple fields
  const parseFieldNameForStageKey = useCallback((fieldName) => {
    // Check if this is a nested substage field (contains ":" and ends with ")")
    if (fieldName.includes(":") && fieldName.endsWith(")")) {
      const colonIndex = fieldName.indexOf(":");
      if (colonIndex > 0) {
        const stageKey = fieldName.substring(0, colonIndex).trim();
        return stageKey;
      }
    }

    // Simple field - format: "stageKey_fieldName" (e.g., "main_information_CATEGORY")
    if (fieldName.includes("_")) {
      const underscoreIndex = fieldName.indexOf("_");
      if (underscoreIndex > 0) {
        const stageKey = fieldName.substring(0, underscoreIndex).trim();
        return stageKey;
      }
    }

    return null;
  }, []);

  // Helper to map field names to stageKey (fallback for legacy fields)
  const getStageKeyForField = useCallback((fieldName) => {
    // First try parsing from field name format
    const parsedStageKey = parseFieldNameForStageKey(fieldName);
    if (parsedStageKey) {
      return parsedStageKey;
    }

    // Fallback to heuristics for legacy fields
    const field = fieldName.toLowerCase();

    // TC Certificate
    if (
      field.includes("(mandatory) tc") ||
      field.includes("tier 1") ||
      field.includes("certificate")
    ) {
      return "tc_certificate";
    }
    // Material Specification
    if (
      field.includes("primary") ||
      field.includes("2nd") ||
      field.includes("3rd") ||
      field.includes("contrast") ||
      field.includes("logo") ||
      field.includes("print")
    ) {
      return "material_specification";
    }
    // Fabric Approval (including comments)
    if (
      field.includes("lab dips") ||
      field.includes("bulk fabric") ||
      field.includes("fabric approval")
    ) {
      return "fabric_approval";
    }
    // Trim Approval (including comments)
    if (
      field.includes("trim smpl") ||
      field.includes("bulk trim") ||
      field.includes("trim approval")
    ) {
      return "trim_approval";
    }
    // Sample Status (including comments)
    if (
      field.includes("fit") ||
      field.includes("pp") ||
      field.includes("top arrival") ||
      field.includes("marketing top") ||
      field.includes("sample status")
    ) {
      return "sample_status";
    }
    // Testing
    if (
      field.includes("fabric testing") ||
      field.includes("garment testing") ||
      field.includes("testing")
    ) {
      return "testing";
    }
    // Production
    if (
      field.includes("cut date") ||
      field.includes("sewing") ||
      field.includes("production")
    ) {
      return "production";
    }
    // Inspection
    if (
      field.includes("inspection") ||
      field.includes("3rd party") ||
      field.includes("final inspection")
    ) {
      return "inspection";
    }
    // Logistics
    if (
      field.includes("logistics") ||
      field.includes("vessel") ||
      field.includes("cargo") ||
      field.includes("on board") ||
      field.includes("in dc")
    ) {
      return "logistics";
    }
    // General Comments
    if (field.includes("comment") && !field.includes("approval")) {
      return "general_comments";
    }
    // Main Information
    if (
      field.includes("category") ||
      field.includes("vendor style") ||
      field.includes("roots style") ||
      field.includes("po #") ||
      field.includes("customer") ||
      field.includes("units") ||
      field.includes("description") ||
      field.includes("colour") ||
      field.includes("factory")
    ) {
      return "main_information";
    }

    return null;
  }, []);

  // Helper function to check if a field should be editable based on lock state and readOnly prop
  const isFieldEditable = useCallback(
    (fieldName) => {
      const fieldUpper = fieldName.toUpperCase();

      // Supplier-facing platform restrictions (readOnly = true)
      if (readOnly) {
        // 1. Never allow STATUS fields to be editable
        if (fieldUpper.includes("(STATUS)")) {
          return false;
        }

        // 2. Never allow COMMENT fields to be editable
        if (fieldUpper.includes("(COMMENT)")) {
          return false;
        }

        // 3. REQ dates: editable when dates are unlocked, not editable when locked
        if (fieldUpper.includes("(REQ)")) {
          return !lockedState?.state; // Editable if NOT locked
        }

        // 4. ACTUAL dates: always editable
        if (fieldUpper.includes("(ACTUAL)")) {
          return true;
        }

        // All other fields are not editable for suppliers
        return false;
      }

      // Non-supplier view (x-auditsense platform)
      // If not locked, all fields are editable
      if (!lockedState?.state) {
        return true;
      }

      // If locked, only ACTUAL fields are editable
      // Disable REQ, CONTROL, STATUS, and COMMENT fields
      if (
        fieldUpper.includes("(REQ)") ||
        fieldUpper.includes("(CONTROL)") ||
        fieldUpper.includes("(STATUS)") ||
        fieldUpper.includes("(COMMENT)")
      ) {
        return false;
      }

      // Allow ACTUAL fields and other fields (like main info fields)
      return true;
    },
    [lockedState, readOnly]
  );

  // Handle comment editing - same as wipTrackerDetails
  const handleEditComment = useCallback(
    (row, field) => {
      // For comment fields, always allow opening the modal (even in read-only mode for viewing)
      // For other fields, check if editable
      const isCommentField = field.includes("(COMMENT)");
      if (!isCommentField && !isFieldEditable(field)) {
        return;
      }

      const clonedRow = JSON.parse(JSON.stringify(row));

      setCommentModalData({
        row: clonedRow,
        field,
        approvalInfo: null,
      });
      commentModalDataRef.current = {
        row: clonedRow,
        field,
        approvalInfo: null,
      };
      setCommentModalOpen(true);
    },
    [isFieldEditable, readOnly]
  );

  // Simple comparison function - normalize dates to ISO strings for comparison
  const valuesAreEqual = useCallback((val1, val2) => {
    // Both null/undefined
    if (
      (val1 === null || val1 === undefined) &&
      (val2 === null || val2 === undefined)
    ) {
      return true;
    }
    // One is null/undefined, other isn't
    if (
      (val1 === null || val1 === undefined) !==
      (val2 === null || val2 === undefined)
    ) {
      return false;
    }

    // Normalize both to ISO strings for comparison
    const normalize = (val) => {
      if (val instanceof Date) return val.toISOString();
      if (typeof val === "string") {
        const date = new Date(val);
        return !isNaN(date.getTime()) ? date.toISOString() : val;
      }
      return val;
    };

    return normalize(val1) === normalize(val2);
  }, []);

  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      // Defensive check - ensure row exists and has an id
      if (!newRow) {
        // If newRow is null/undefined, return oldRow if available, otherwise return null
        return oldRow || null;
      }

      if (!newRow.id) {
        console.warn(
          "⚠️ processRowUpdate: Row missing id, skipping update",
          newRow
        );
        return oldRow || newRow;
      }

      // Ensure oldRow exists for comparison
      if (!oldRow) {
        console.warn(
          "⚠️ processRowUpdate: Old row missing, using newRow as oldRow",
          newRow
        );
        oldRow = { ...newRow };
      }

      const rowId = newRow.id;

      // Check which field changed - use proper date comparison
      const changedField = Object.keys(newRow).find((key) => {
        // Skip internal fields
        if (
          key === "id" ||
          key === "_id" ||
          key === "actions" ||
          key.endsWith("_iso")
        ) {
          return false;
        }
        return !valuesAreEqual(newRow[key], oldRow[key]);
      });

      // Prevent changes to locked fields (defensive check)
      if (changedField && !isFieldEditable(changedField)) {
        console.warn(
          "⚠️ processRowUpdate: Attempted to edit locked field:",
          changedField
        );
        // Revert the change by returning oldRow
        return oldRow;
      }

      // If a status field was changed to "Rejected", open the comment modal
      // (Only for non-supplier views - suppliers cannot edit status or comments)
      if (
        !readOnly &&
        changedField &&
        changedField.includes("Status") &&
        newRow[changedField] === "Rejected" &&
        oldRow[changedField] !== "Rejected"
      ) {
        // Map status field to comment field
        const commentFieldMap = {
          "FABRIC APPROVAL: LAB DIPS/STRIKE OFF/ KNIT DOWN (STATUS)":
            "FABRIC APPROVAL: LAB DIPS/STRIKE OFF/ KNIT DOWN (COMMENT)",
          "FABRIC APPROVAL: BULK FABRIC (STATUS)":
            "FABRIC APPROVAL: BULK FABRIC (COMMENT)",
          "TRIM APPROVAL: TRIM SMPL (STATUS)":
            "TRIM APPROVAL: TRIM SMPL (COMMENT)",
          "TRIM APPROVAL: BULK TRIM (STATUS)":
            "TRIM APPROVAL: BULK TRIM (COMMENT)",
          "SAMPLE STATUS: FIT (STATUS)": "SAMPLE STATUS: FIT (COMMENT)",
          "SAMPLE STATUS: PP (STATUS)": "SAMPLE STATUS: PP (COMMENT)",
          "SAMPLE STATUS: TOP ARRIVAL DEADLINE (STATUS)":
            "SAMPLE STATUS: TOP ARRIVAL DEADLINE (COMMENT)",
          "INSPECTION: 3RD PARTY FINAL INSPECTION DATE (STATUS)":
            "INSPECTION: 3RD PARTY FINAL INSPECTION DATE (COMMENT)",
        };

        const commentField = commentFieldMap[changedField];
        if (commentField) {
          setTimeout(() => {
            handleEditComment(newRow, commentField);
          }, 100);
        }
      }

      // Store the updated row as-is (Date objects are fine)
      unsavedChangesRef.current.unsavedRows[rowId] = { ...newRow };
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = { ...oldRow };
      }

      setHasUnsavedRows(true);

      // Reset auto-save timer on edit
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set auto-save timer for 5 seconds
      autoSaveTimerRef.current = setTimeout(() => {
        // Check if there are still unsaved changes before auto-saving
        const hasUnsaved =
          Object.keys(unsavedChangesRef.current.unsavedRows).length > 0;
        if (hasUnsaved && !saving && handleSaveChangesRef.current) {
          console.log(
            "🔄 Auto-saving changes after 5 seconds of inactivity..."
          );
          handleSaveChangesRef.current(false);
        }
      }, 5000);

      return newRow;
    },
    [handleEditComment, valuesAreEqual, isFieldEditable, readOnly]
  );

  // Handle comment save - update row and mark as unsaved (same as wipTrackerDetails)
  const handleSaveComments = useCallback(
    (comments) => {
      // Use ref instead of state to avoid closure issues
      const { row, field } = commentModalDataRef.current;

      if (!row || !field) {
        console.warn("❌ Missing row or field");
        return;
      }

      // Update the row with new comments
      const updatedRow = {
        ...row,
        [field]: comments, // Comments array
      };

      // Mark as unsaved
      const rowId = updatedRow.id;
      unsavedChangesRef.current.unsavedRows[rowId] = updatedRow;
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = row;
      }

      // Update DataGrid row
      apiRef.current?.updateRows([updatedRow]);
      setHasUnsavedRows(true);

      // Reset auto-save timer on comment save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set auto-save timer for 5 seconds
      autoSaveTimerRef.current = setTimeout(() => {
        // Check if there are still unsaved changes before auto-saving
        const hasUnsaved =
          Object.keys(unsavedChangesRef.current.unsavedRows).length > 0;
        if (hasUnsaved && !saving && handleSaveChangesRef.current) {
          console.log(
            "🔄 Auto-saving changes after 5 seconds of inactivity..."
          );
          handleSaveChangesRef.current(false);
        }
      }, 5000);

      // Modal will close itself via onClose()
    },
    [apiRef, saving]
  );

  // Handle save changes - send all updates grouped by item ID
  const handleSaveChanges = useCallback(
    async (isManualSave = false) => {
      // Track if this is an auto-save using ref (for synchronous access)
      isAutoSaveRef.current = !isManualSave;
      console.log(
        "🔴 === handleSaveChanges (Batch View) START ===",
        "isManualSave:",
        isManualSave,
        "isAutoSaveRef.current:",
        isAutoSaveRef.current
      );
      // Collect all unsaved rows
      const unsavedRows = Object.values(unsavedChangesRef.current.unsavedRows);
      const originalRows = unsavedChangesRef.current.rowsBeforeChange;

      // For each unsaved row, create updates array in backend format
      const allUpdates = [];

      unsavedRows.forEach((newRow, rowIdx) => {
        const oldRow = originalRows[newRow.id] || {};
        const rowId = newRow._id || newRow.id;

        // Find changed fields
        Object.keys(newRow).forEach((fieldName) => {
          if (
            fieldName === "id" ||
            fieldName === "_id" ||
            fieldName === "actions"
          )
            return;

          const newValue = newRow[fieldName];
          const oldValue = oldRow[fieldName];

          // Check if this is a comment field
          const isCommentField = fieldName.toLowerCase().includes("comment");

          if (isCommentField) {
            // For comment arrays, check if new comments were added
            const oldComments = Array.isArray(oldValue) ? oldValue : [];
            const newComments = Array.isArray(newValue) ? newValue : [];
            const hasChanged = newComments.length > oldComments.length;

            if (hasChanged) {
              const stageKey = getStageKeyForField(fieldName);
              if (stageKey) {
                // Send only NEW comments individually
                const newCommentsToSend = newComments.slice(oldComments.length);
                newCommentsToSend.forEach((commentObj) => {
                  allUpdates.push({
                    wipItemId: rowId,
                    stageKey,
                    stepField: fieldName,
                    value: commentObj, // Single comment object
                  });
                });
              }
            }
          } else {
            // Non-comment field - use proper date comparison
            const hasChanged = !valuesAreEqual(newValue, oldValue);

            if (hasChanged) {
              const stageKey = getStageKeyForField(fieldName);
              if (stageKey) {
                let value = newValue;
                // Convert Date objects to ISO strings for backend
                if (value instanceof Date) {
                  value = value.toISOString();
                }
                // Also handle if value is already an ISO string (from paste)
                else if (typeof value === "string") {
                  const dateValue = new Date(value);
                  if (!isNaN(dateValue.getTime())) {
                    value = dateValue.toISOString();
                  }
                }

                allUpdates.push({
                  wipItemId: rowId,
                  stageKey,
                  stepField: fieldName,
                  value,
                });
              }
            }
          }
        });
      });

      // Group updates by wipItemId
      const updatesByItem = allUpdates.reduce((acc, update) => {
        const { wipItemId, ...updateData } = update;
        if (!acc[wipItemId]) {
          acc[wipItemId] = [];
        }
        acc[wipItemId].push(updateData);
        return acc;
      }, {});

      // Create batch updates array in the format: [{ wipItemId, updates }, ...]
      const batchUpdates = Object.entries(updatesByItem).map(
        ([wipItemId, updates]) => ({
          wipItemId,
          updates, // Use 'updates' not 'stageUpdates' - saga will parse and convert to stageUpdates/nestedUpdates
        })
      );

      // Dispatch single batch update call
      dispatch(
        bulkUpdateWipDetails({
          batchUpdates,
        })
      );

      // Clear unsaved changes
      setHasUnsavedRows(false);
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      };

      // Clear auto-save timer when manual save is triggered
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      // Don't refresh data - snackbar will show success/error instead
      console.log("=== handleSaveChanges (Batch View) END ===");
    },
    [dispatch, getStageKeyForField, parseFieldNameForStageKey, valuesAreEqual]
  );

  // Update ref whenever handleSaveChanges changes
  useEffect(() => {
    handleSaveChangesRef.current = handleSaveChanges;
  }, [handleSaveChanges]);

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    setHasUnsavedRows(false);
    apiRef.current?.updateRows(
      Object.values(unsavedChangesRef.current.rowsBeforeChange)
    );
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };

    // Clear auto-save timer when discarding changes
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, [apiRef]);

  // Get row class name for edited rows
  const getRowClassName = useCallback(({ id }) => {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[id];
    if (unsavedRow) {
      return "row--edited";
    }
    return "";
  }, []);

  // Handle comment modal close
  const handleCloseCommentModal = useCallback(() => {
    setCommentModalOpen(false);
    setCommentModalData({ row: null, field: null, approvalInfo: null });
  }, []);

  // Handle cell selection/deselection
  const handleCellSelection = useCallback((field, rowId, checked) => {
    setSelectedCells((prev) => {
      const newMap = new Map(prev);
      if (!newMap.has(field)) {
        newMap.set(field, new Set());
      }
      const cellSet = newMap.get(field);
      if (checked) {
        cellSet.add(rowId);
      } else {
        cellSet.delete(rowId);
        if (cellSet.size === 0) {
          newMap.delete(field);
        }
      }
      return newMap;
    });
  }, []);

  // Handle column header checkbox (select/deselect all cells in column)
  const handleColumnHeaderCheckbox = useCallback(
    (field, checked) => {
      setSelectedCells((prev) => {
        const newMap = new Map(prev);
        if (checked) {
          // Select all cells in this column
          const allRowIds = new Set(items.map((item) => item.id));
          newMap.set(field, allRowIds);
        } else {
          // Deselect all cells in this column
          newMap.delete(field);
        }
        return newMap;
      });
    },
    [items]
  );

  // Check if all cells in a column are selected
  const isColumnFullySelected = useCallback(
    (field) => {
      const selectedSet = selectedCells.get(field);
      if (!selectedSet || selectedSet.size === 0) return false;
      return selectedSet.size === items.length;
    },
    [selectedCells, items]
  );

  // Check if a cell is selected
  const isCellSelected = useCallback(
    (field, rowId) => {
      const selectedSet = selectedCells.get(field);
      return selectedSet ? selectedSet.has(rowId) : false;
    },
    [selectedCells]
  );

  // Get columns that have selected cells
  const getColumnsWithSelections = useCallback(() => {
    return Array.from(selectedCells.keys()).filter(
      (field) => selectedCells.get(field)?.size > 0
    );
  }, [selectedCells]);

  // Get count of selected cells for a specific field
  const getSelectedCount = useCallback(
    (field) => {
      const selectedSet = selectedCells.get(field);
      return selectedSet ? selectedSet.size : 0;
    },
    [selectedCells]
  );

  // Handle bulk update for selected cells
  const handleBulkUpdate = useCallback(
    (field, value) => {
      const selectedSet = selectedCells.get(field);
      if (!selectedSet || selectedSet.size === 0) {
        console.warn("No cells selected for bulk update");
        return;
      }

      // Determine the column type to convert value appropriately
      const column = wipColumnsRef.current.find((col) => col.field === field);
      let convertedValue = value;
      const isCommentField = field.includes("(COMMENT)");

      // Convert value based on column type
      if (column?.type === "date") {
        // For date columns, try to parse the value
        if (value && typeof value === "string") {
          const dateValue = new Date(value);
          convertedValue = !isNaN(dateValue.getTime())
            ? dateValue.toISOString()
            : value;
        } else if (value instanceof Date) {
          convertedValue = value.toISOString();
        }
      } else if (column?.type === "number") {
        // For number columns, convert to number
        const numValue = parseFloat(value);
        convertedValue = !isNaN(numValue) ? numValue : value;
      } else if (isCommentField && value) {
        // For comment fields, format as comment array structure
        // Comments are stored as arrays: [{ comment: string, userId: string, userName: string, timestamp: string }]
        if (!currentUserId || !currentUserName) {
          console.warn("Cannot add comment: missing userId or userName");
          return; // Skip bulk update if user info is missing for comments
        }
        const commentEntry = {
          comment: value,
          userId: currentUserId,
          userName: currentUserName,
          timestamp: new Date().toISOString(),
        };
        // If there are existing comments, append; otherwise create new array
        convertedValue = [commentEntry];
      }

      // Update all selected cells
      const updatedRows = [];
      selectedSet.forEach((rowId) => {
        const row = items.find((item) => item.id === rowId);
        if (row) {
          let updatedRow;

          if (isCommentField && value) {
            // For comments, merge with existing comments array
            // Note: userId/userName check already done above, skip this row if missing
            if (!currentUserId || !currentUserName) {
              return; // Skip this row
            }
            const existingComments = Array.isArray(row[field])
              ? row[field]
              : [];
            const commentEntry = {
              comment: value,
              userId: currentUserId,
              userName: currentUserName,
              timestamp: new Date().toISOString(),
            };
            updatedRow = {
              ...row,
              [field]: [...existingComments, commentEntry],
            };
          } else {
            updatedRow = { ...row, [field]: convertedValue };
          }

          updatedRows.push(updatedRow);

          // Mark as unsaved
          unsavedChangesRef.current.unsavedRows[rowId] = updatedRow;
          if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
            unsavedChangesRef.current.rowsBeforeChange[rowId] = { ...row };
          }
        }
      });

      // Update DataGrid
      if (updatedRows.length > 0) {
        apiRef.current?.updateRows(updatedRows);
        setHasUnsavedRows(true);

        // Clear selection after update
        setSelectedCells((prev) => {
          const newMap = new Map(prev);
          newMap.delete(field);
          return newMap;
        });

        // Reset auto-save timer
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(() => {
          const hasUnsaved =
            Object.keys(unsavedChangesRef.current.unsavedRows).length > 0;
          if (hasUnsaved && !saving && handleSaveChangesRef.current) {
            handleSaveChangesRef.current(false);
          }
        }, 5000);
      }
    },
    [selectedCells, items, saving, currentUserId, currentUserName, apiRef]
  );

  // Create columns dynamically based on actual row data
  const wipColumns = useMemo(() => {
    if (!items || items.length === 0) {
      console.warn("🔍 [WIPBatchList] No items, returning empty columns");
      return [];
    }
    // Pass readOnly flag to use chip columns for STATUS fields in supplier view
    const createdColumns = createWipColumnsDynamic(
      items,
      handleEditComment,
      {
        readOnly,
        lockedState,
        onCellSelection: handleCellSelection,
        onColumnHeaderCheckbox: handleColumnHeaderCheckbox,
        isCellSelected: isCellSelected,
        isColumnFullySelected: isColumnFullySelected,
        getSelectedCount: getSelectedCount,
      }
    );

    // Apply lock state restrictions to columns
    // Preserve STATUS chip columns (for supplier view) - they should always be read-only
    return createdColumns.map((col) => {
      // Check if this is a STATUS field (chip column in supplier view)
      const isStatusField = col.field && col.field.includes("(STATUS)");

      if (isStatusField && readOnly) {
        // STATUS fields are always read-only chips in supplier view
        return {
          ...col,
          editable: false,
        };
      }

      // For all other columns, apply editable restrictions based on field type and lock state
      return {
        ...col,
        editable: col.editable && isFieldEditable(col.field),
      };
    });
  }, [
    items,
    handleEditComment,
    isFieldEditable,
    handleCellSelection,
    handleColumnHeaderCheckbox,
    isCellSelected,
    isColumnFullySelected,
    getSelectedCount,
    readOnly,
    lockedState,
  ]);

  // Update wipColumnsRef whenever wipColumns changes
  useEffect(() => {
    wipColumnsRef.current = wipColumns;
  }, [wipColumns]);

  // Standard stage order: main_information first, then material, then tc, then others
  const STANDARD_STAGE_ORDER = [
    {
      stageKey: "main_information",
      displayName: "Main Information",
      groupId: "mainInfo",
      headerName: "MAIN INFORMATION",
    },
    {
      stageKey: "material_specification",
      displayName: "Material Specification",
      groupId: "material",
      headerName: "MATERIAL SPECIFICATION",
    },
    {
      stageKey: "tc_certificate",
      displayName: "TC Certificate",
      groupId: "tc",
      headerName: "TC (TRACEABILITY CERTIFICATE)",
    },
    {
      stageKey: "fabric_approval",
      displayName: "Fabric Approval",
      groupId: "fabricApproval",
      headerName: "FABRIC APPROVAL",
    },
    {
      stageKey: "trim_approval",
      displayName: "Trim Approval",
      groupId: "trimApproval",
      headerName: "TRIM APPROVAL",
    },
    {
      stageKey: "sample_status",
      displayName: "Sample Status",
      groupId: "sampleStatus",
      headerName: "SAMPLE STATUS",
    },
    {
      stageKey: "testing",
      displayName: "Testing",
      groupId: "testing",
      headerName: "TESTING",
    },
    {
      stageKey: "production",
      displayName: "Production",
      groupId: "production",
      headerName: "PRODUCTION",
    },
    {
      stageKey: "inspection",
      displayName: "Inspection",
      groupId: "inspection",
      headerName: "INSPECTION",
    },
    {
      stageKey: "logistics",
      displayName: "Logistics",
      groupId: "logisticsDates",
      headerName: "LOGISTICS DATES",
    },
  ];

  // Build dynamic column grouping model from actual columns
  // Maintain backend order using STANDARD_STAGE_ORDER
  const wipColumnGroupingModel = useMemo(() => {
    if (!wipColumns || wipColumns.length === 0) return [];

    const groups = [];

    // Track processed groupIds and fields to prevent duplicates
    const processedGroupIds = new Set();
    const processedFields = new Set();

    // Process stages in STANDARD_STAGE_ORDER
    STANDARD_STAGE_ORDER.forEach((stage) => {
      // Check for simple fields (stageKey_fieldName format)
      const simpleFields = wipColumns
        .filter((col) => col.field.startsWith(`${stage.stageKey}_`))
        .map((col) => ({ field: col.field }));

      // Check for nested fields (stageKey: substageKey format)
      const nestedFields = wipColumns
        .filter((col) => col.field.startsWith(`${stage.stageKey}:`))
        .map((col) => ({ field: col.field }));

      const allFields = [...simpleFields, ...nestedFields];

      if (allFields.length > 0 && !processedGroupIds.has(stage.groupId)) {
        groups.push({
          groupId: stage.groupId,
          headerName: stage.headerName,
          children: allFields,
        });
        processedGroupIds.add(stage.groupId);
        // Mark these fields as processed
        allFields.forEach((f) => processedFields.add(f.field));
      }
    });

    // Also include any fields from stages not in STANDARD_STAGE_ORDER (maintain backend order)
    const processedStageKeys = new Set(
      STANDARD_STAGE_ORDER.map((s) => s.stageKey)
    );
    const remainingFields = new Map();

    wipColumns.forEach((col) => {
      // Skip if field was already processed
      if (processedFields.has(col.field)) return;

      // Skip if already processed in STANDARD_STAGE_ORDER
      let alreadyProcessed = false;
      for (const stage of STANDARD_STAGE_ORDER) {
        if (
          col.field.startsWith(`${stage.stageKey}_`) ||
          col.field.startsWith(`${stage.stageKey}:`)
        ) {
          alreadyProcessed = true;
          break;
        }
      }
      if (alreadyProcessed) return;

      // Extract stageKey from field
      let stageKey = null;
      if (col.field.includes(":")) {
        stageKey = col.field.split(":")[0].trim();
      } else if (col.field.includes("_")) {
        const parts = col.field.split("_");
        // Try to find a stage key (check longest matches first)
        for (let i = parts.length - 1; i >= 1; i--) {
          const possibleStageKey = parts.slice(0, i).join("_");
          if (
            !processedStageKeys.has(possibleStageKey) &&
            !col.field.endsWith("_status") &&
            !col.field.endsWith("_daysLate") &&
            !col.field.endsWith("_displayName") &&
            !col.field.endsWith("_id")
          ) {
            stageKey = possibleStageKey;
            break;
          }
        }
      }

      if (stageKey && !processedStageKeys.has(stageKey)) {
        if (!remainingFields.has(stageKey)) {
          remainingFields.set(stageKey, []);
        }
        remainingFields.get(stageKey).push({ field: col.field });
      }
    });

    // Add remaining fields in their original order (use unique groupId)
    remainingFields.forEach((fields, stageKey) => {
      // Generate a unique groupId for remaining stages
      const groupId = stageKey.replace(/_/g, "");
      if (!processedGroupIds.has(groupId)) {
        groups.push({
          groupId: groupId,
          headerName: stageKey.toUpperCase().replace(/_/g, " "),
          children: fields,
        });
        processedGroupIds.add(groupId);
      }
    });

    // Add general comments group if COMMENTS field exists
    const commentsFields = wipColumns
      .filter(
        (col) =>
          col.field === "COMMENTS" ||
          (col.field.includes("COMMENT") && !col.field.includes(":"))
      )
      .map((col) => ({ field: col.field }));

    if (
      commentsFields.length > 0 &&
      !processedGroupIds.has("generalComments")
    ) {
      groups.push({
        groupId: "generalComments",
        headerName: "GENERAL COMMENTS",
        children: commentsFields,
      });
      processedGroupIds.add("generalComments");
    }

    // Add headerClassName for colors
    return groups.map((group) => ({
      ...group,
      headerClassName: `group-header-${group.groupId}`,
    }));
  }, [wipColumns]);

  // Identify REQ and ACTUAL columns for color coding (same as wipTrackerDetails)
  const reqColumns = useMemo(
    () =>
      wipColumns
        .filter((col) => col.headerName.includes("(REQ)"))
        .map((col) => col.field),
    [wipColumns]
  );

  const actualColumns = useMemo(() => {
    const columns = wipColumns
      .filter((col) => col.field.includes("(ACTUAL)"))
      .map((col) => col.field);

    return columns;
  }, [wipColumns]);

  // Approval status-date-comment groups (purple highlighting)
  // Dynamically find all STATUS columns
  const approvalColumns = useMemo(() => {
    return wipColumns
      .filter((col) => col.field && col.field.includes("(STATUS)"))
      .map((col) => col.field);
  }, [wipColumns]);

  // CONTROL columns for past due highlighting
  const controlColumns = useMemo(() => {
    return wipColumns
      .filter((col) => col.field && col.field.includes("(CONTROL)"))
      .map((col) => col.field);
  }, [wipColumns]);

  // Non-approval ACTUAL columns (green highlighting)
  const nonApprovalActualColumns = useMemo(
    () => actualColumns.filter((col) => !approvalColumns.includes(col)),
    [actualColumns, approvalColumns]
  );

  // PERFORMANCE OPTIMIZATION: Pre-calculate REQ column header styles
  const reqColumnHeaderStyles = useMemo(() => {
    if (!highlightReqActual) return {};
    return reqColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
        backgroundColor: "#F5F5F5",
      };
      return acc;
    }, {});
  }, [highlightReqActual, reqColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate approval column header styles
  const approvalColumnHeaderStyles = useMemo(() => {
    if (!highlightReqActual) return {};
    return approvalColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
        backgroundColor: "#F3E5F5",
      };
      return acc;
    }, {});
  }, [highlightReqActual, approvalColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate ACTUAL column header styles
  const actualColumnHeaderStyles = useMemo(() => {
    if (!highlightActual) return {};
    return nonApprovalActualColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
        backgroundColor: "#E8F5E9",
      };
      return acc;
    }, {});
  }, [highlightActual, nonApprovalActualColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate REQ cell styles
  const reqCellStyles = useMemo(() => {
    if (!highlightReqActual) return {};
    return reqColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-cell[data-field="${field}"]`] = {
        backgroundColor: "#F5F5F5",
        "&:hover": {
          backgroundColor: "#E8E8E8",
        },
      };
      return acc;
    }, {});
  }, [highlightReqActual, reqColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate approval cell styles
  const approvalCellStyles = useMemo(() => {
    if (!highlightReqActual) return {};
    return approvalColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-cell[data-field="${field}"]`] = {
        backgroundColor: "#F3E5F5",
        "&:hover": {
          backgroundColor: "#E1BEE7",
        },
      };
      return acc;
    }, {});
  }, [highlightReqActual, approvalColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate ACTUAL cell styles
  const actualCellStyles = useMemo(() => {
    if (!highlightActual) return {};
    return nonApprovalActualColumns.reduce((acc, field) => {
      acc[`& .MuiDataGrid-cell[data-field="${field}"]`] = {
        backgroundColor: "#E8F5E9",
        "&:hover": {
          backgroundColor: "#C8E6C9",
        },
      };
      return acc;
    }, {});
  }, [highlightActual, nonApprovalActualColumns]);

  // PERFORMANCE OPTIMIZATION: Pre-calculate past due cell styles
  const pastDueCellStyles = useMemo(() => {
    if (!highlightPastDue) return {};
    return controlColumns.reduce((acc, field) => {
      acc[
        `& .MuiDataGrid-cell[data-field="${field}"]:has([data-value="past-due"])`
      ] = {
        backgroundColor: "#ffcdd2",
        "&:hover": {
          backgroundColor: "#ef9a9a",
        },
      };
      return acc;
    }, {});
  }, [highlightPastDue, controlColumns]);

  // Get cell class name for past due highlighting
  const getCellClassName = useCallback(
    (params) => {
      if (!highlightPastDue) return "";

      // Check if this is a CONTROL column with past-due marker
      if (controlColumns.includes(params.field)) {
        // Check if the cell contains a past-due marker (set by createControlColumn)
        const cellElement = document.querySelector(
          `[data-field="${params.field}"][data-id="${params.id}"]`
        );
        if (cellElement?.querySelector('[data-value="past-due"]')) {
          return "cell--past-due";
        }
      }

      return "";
    },
    [highlightPastDue, controlColumns]
  );

  // Column group buttons
  const columnGroupButtons = [
    { id: "mainInfo", label: "Main Info" },
    { id: "tc", label: "TC" },
    { id: "material", label: "Material" },
    { id: "fabricApproval", label: "Fabric Approval" },
    { id: "trimApproval", label: "Trim Approval" },
    { id: "sampleStatus", label: "Sample Status" },
    { id: "testing", label: "Testing" },
    { id: "production", label: "Production" },
    { id: "inspection", label: "Inspection" },
    { id: "logisticsDates", label: "Logistics" },
    { id: "generalComments", label: "Comments" },
  ];

  // Filter column grouping model based on visible groups
  const visibleColumnGroupingModel = useMemo(() => {
    return wipColumnGroupingModel
      .filter((group) => visibleGroups[group.groupId])
      .map((group) => ({
        ...group,
        headerClassName: `group-header-${group.groupId}`,
      }));
  }, [visibleGroups, wipColumnGroupingModel]);

  // Filter columns based on visible groups
  const STANDALONE_FIELDS = ["department", "season"];

  const visibleColumns = useMemo(() => {
    const visibleGroupIds = Object.keys(visibleGroups).filter(
      (key) => visibleGroups[key]
    );

    const visibleFields = visibleColumnGroupingModel
      .filter((group) => visibleGroupIds.includes(group.groupId))
      .flatMap((group) => group.children.map((child) => child.field));

    return wipColumns.filter(
      (col) => STANDALONE_FIELDS.includes(col.field) || visibleFields.includes(col.field)
    );
  }, [visibleGroups, visibleColumnGroupingModel, wipColumns]);

  // Columns without actions column (use visibleColumns for DataGrid)
  const columns = useMemo(() => {
    return visibleColumns;
  }, [visibleColumns]);

  // Toggle group visibility
  const toggleGroupVisibility = useCallback((groupId) => {
    setVisibleGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  // Handle frozen columns change
  const handleFrozenColumnsChange = (event) => {
    const {
      target: { value },
    } = event;
    setFrozenColumns(typeof value === "string" ? value.split(",") : value);
  };

  // Available columns for freezing
  const freezableColumns = useMemo(() => {
    return wipColumns.map((col) => ({
      field: col.field,
      label: col.headerName,
    }));
  }, [wipColumns]);

  // Quick navigation to sections - scroll to column group
  const scrollToColumnGroup = useCallback(
    (groupId) => {
      const group = wipColumnGroupingModel.find((g) => g.groupId === groupId);
      if (group && group.children.length > 0) {
        const firstField = group.children[0].field;

        // Calculate width of frozen columns
        let frozenColumnsWidth = 0;
        for (const frozenField of frozenColumns) {
          const frozenCol = visibleColumns.find(
            (col) => col.field === frozenField
          );
          if (frozenCol) {
            frozenColumnsWidth += frozenCol.width || 100;
          }
        }

        // Get all column fields up to this point to calculate scroll position
        let scrollPosition = 0;
        let foundTarget = false;

        for (const col of visibleColumns) {
          if (col.field === firstField) {
            foundTarget = true;
            break;
          }
          // Don't add width of frozen columns to scroll position
          if (!frozenColumns.includes(col.field)) {
            scrollPosition += col.width || 100;
          }
        }

        if (foundTarget) {
          const gridElement = apiRef.current?.rootElementRef?.current;
          const virtualScroller = gridElement?.querySelector(
            ".MuiDataGrid-virtualScroller"
          );
          if (virtualScroller) {
            virtualScroller.scrollLeft = scrollPosition;
          }
        }
      }
    },
    [visibleColumns, frozenColumns, wipColumnGroupingModel]
  );

  // Only check for selectedBatchId if items are coming from Redux (not provided via props)
  if (!itemsProp && !selectedBatchId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Please select a batch date to view WIP items
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading WIP items...
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No WIP items found for this batch
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with Quick Actions - Collapsible */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: headerExpanded ? 1.5 : 1,
          flexShrink: 0,
          borderBottom: "1px solid #e5e7eb",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          width: "80%",
          alignSelf: "center",
          transition: "all 0.3s ease-in-out",
          position: "relative",
        }}
      >
        {/* Collapse/Expand Button - Top Right */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <Button
            onClick={() => {
              setHeaderExpanded(!headerExpanded);
            }}
            variant="outlined"
            size="small"
            startIcon={headerExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              borderColor: "#4B68D4",
              color: "#4B68D4",
              backgroundColor: "#EEF2FF",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#E0E7FF",
                borderColor: "#3d56b3",
              },
            }}
          >
            {headerExpanded ? "Collapse" : "Expand"}
          </Button>
        </Box>

        {/* Title and Save/Discard Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: headerExpanded ? 2 : 0,
            pr: 12, // Add padding to prevent overlap with collapse button
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
              {itemsProp ? items.length : pagination.total} Item
              {(itemsProp ? items.length : pagination.total) !== 1 ? "s" : ""}
            </Typography>
            {headerExpanded && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {itemsProp
                  ? "View and edit WIP item details"
                  : "Edit multiple items at once. Changes are saved per item."}
              </Typography>
            )}
          </Box>

          {/* Save/Discard Buttons and Bulk Update */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Bulk Update Section */}
            {getColumnsWithSelections().length > 0 && (
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mr: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#667085", fontWeight: 500 }}
                >
                  {getColumnsWithSelections().length} column
                  {getColumnsWithSelections().length > 1 ? "s" : ""} selected
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    // Open bulk update dialog for first selected column
                    const firstField = getColumnsWithSelections()[0];
                    const column = wipColumnsRef.current.find(
                      (c) => c.field === firstField
                    );
                    const sampleRow = items.find(
                      (item) =>
                        item[firstField] !== undefined &&
                        item[firstField] !== null
                    );
                    const currentValue = sampleRow?.[firstField];
                    const isCommentField = firstField.includes("(COMMENT)");

                    setBulkUpdateField(firstField);

                    // Set initial value based on column type
                    if (column?.type === "date") {
                      // For date columns, parse the current value to a Date object
                      if (currentValue instanceof Date) {
                        setBulkUpdateDateValue(currentValue);
                      } else if (
                        typeof currentValue === "string" &&
                        currentValue
                      ) {
                        const dateValue = new Date(currentValue);
                        setBulkUpdateDateValue(
                          isNaN(dateValue.getTime()) ? null : dateValue
                        );
                      } else {
                        setBulkUpdateDateValue(null);
                      }
                      setBulkUpdateValue("");
                    } else if (isCommentField) {
                      // For comment fields, extract the latest comment text
                      if (
                        Array.isArray(currentValue) &&
                        currentValue.length > 0
                      ) {
                        const latestComment =
                          currentValue[currentValue.length - 1];
                        setBulkUpdateValue(latestComment?.comment || "");
                      } else if (typeof currentValue === "string") {
                        setBulkUpdateValue(currentValue);
                      } else {
                        setBulkUpdateValue("");
                      }
                      setBulkUpdateDateValue(null);
                    } else if (
                      column?.type === "singleSelect" &&
                      column?.valueOptions
                    ) {
                      // For status columns, use the current value or empty string
                      setBulkUpdateValue(
                        typeof currentValue === "string"
                          ? currentValue
                          : currentValue
                          ? String(currentValue)
                          : ""
                      );
                      setBulkUpdateDateValue(null);
                    } else {
                      // For other types, use text input
                      setBulkUpdateValue(
                        typeof currentValue === "string"
                          ? currentValue
                          : currentValue
                          ? String(currentValue)
                          : ""
                      );
                      setBulkUpdateDateValue(null);
                    }

                    setBulkUpdateModalOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    backgroundColor: "#10B981",
                    fontSize: "12px",
                    px: 1.5,
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: "#059669",
                    },
                  }}
                >
                  Bulk Update
                </Button>
              </Box>
            )}
          </Box>

          {/* Save/Discard Buttons */}
          {hasUnsavedRows && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={handleDiscardChanges}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#d0d5dd",
                  color: "#667085",
                  "&:hover": {
                    borderColor: "#667085",
                    backgroundColor: "#f5f7fa",
                  },
                }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveChanges(true)}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: "#4B68D4",
                  "&:hover": {
                    backgroundColor: "#3d56b3",
                  },
                }}
              >
                {saving ? "Saving..." : "Save All Changes"}
              </Button>
            </Box>
          )}
        </Box>

        {/* Quick Actions - Collapsible */}
        {headerExpanded && (
          <Box
            sx={{
              mb: 2,
              overflow: "hidden",
              animation: headerExpanded ? "slideDown 0.3s ease-in-out" : "none",
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: "13px",
                color: "#667085",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Quick Action
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                variant={highlightReqActual ? "contained" : "outlined"}
                onClick={() => setHighlightReqActual(!highlightReqActual)}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 500,
                  ...(highlightReqActual
                    ? {
                        backgroundColor: "#4B68D4",
                        boxShadow: "0 2px 8px rgba(75, 104, 212, 0.25)",
                        "&:hover": {
                          backgroundColor: "#3A54B6",
                          boxShadow: "0 4px 12px rgba(75, 104, 212, 0.35)",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }
                    : {
                        borderColor: "#4B68D4",
                        color: "#4B68D4",
                        "&:hover": {
                          backgroundColor: "#EEF2FF",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }),
                }}
              >
                Highlight REQ and STATUS
              </Button>
              <Button
                variant={highlightActual ? "contained" : "outlined"}
                onClick={() => {
                  setHighlightActual(!highlightActual);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 500,
                  ...(highlightActual
                    ? {
                        backgroundColor: "#4B68D4",
                        boxShadow: "0 2px 8px rgba(75, 104, 212, 0.25)",
                        "&:hover": {
                          backgroundColor: "#3A54B6",
                          boxShadow: "0 4px 12px rgba(75, 104, 212, 0.35)",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }
                    : {
                        borderColor: "#4B68D4",
                        color: "#4B68D4",
                        "&:hover": {
                          backgroundColor: "#EEF2FF",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }),
                }}
              >
                Highlight ACTUAL
              </Button>
              <Button
                variant={highlightPastDue ? "contained" : "outlined"}
                onClick={() => setHighlightPastDue(!highlightPastDue)}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 500,
                  ...(highlightPastDue
                    ? {
                        backgroundColor: "#d32f2f",
                        boxShadow: "0 2px 8px rgba(211, 47, 47, 0.25)",
                        "&:hover": {
                          backgroundColor: "#b71c1c",
                          boxShadow: "0 4px 12px rgba(211, 47, 47, 0.35)",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }
                    : {
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        "&:hover": {
                          backgroundColor: "#ffebee",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s ease-in-out",
                        },
                      }),
                }}
              >
                Highlight Past Due
              </Button>

              {/* Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "40px",
                  borderColor: "#e5e7eb",
                  alignSelf: "center",
                }}
              />
            </Box>
          </Box>
        )}

        {/* Section Display - Collapsible */}
        {headerExpanded && (
          <Box sx={{ mb: 0, mt: 1.5 }}>
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: "13px",
                color: "#667085",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Section Display
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Freeze Columns Dropdown */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="freeze-columns-label">
                    Freeze Columns
                  </InputLabel>
                  <Select
                    labelId="freeze-columns-label"
                    id="freeze-columns"
                    multiple
                    value={frozenColumns}
                    onChange={handleFrozenColumnsChange}
                    input={<OutlinedInput label="Freeze Columns" />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return "None";
                      return `${selected.length} column${
                        selected.length > 1 ? "s" : ""
                      }`;
                    }}
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4B68D4",
                      },
                    }}
                  >
                    {freezableColumns.map((column) => (
                      <MenuItem key={column.field} value={column.field}>
                        <Checkbox
                          checked={frozenColumns.indexOf(column.field) > -1}
                        />
                        <ListItemText primary={column.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Unfreeze All Button */}
                <Button
                  variant="outlined"
                  onClick={() => setFrozenColumns([])}
                  disabled={frozenColumns.length === 0}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.75,
                    fontSize: "13px",
                    fontWeight: 500,
                    borderColor: "#d0d5dd",
                    color: "#667085",
                    "&:hover": {
                      borderColor: "#DC6803",
                      color: "#DC6803",
                      backgroundColor: "#FEF3C7",
                    },
                    "&:disabled": {
                      borderColor: "#e5e7eb",
                      color: "#d0d5dd",
                    },
                  }}
                >
                  Unfreeze All
                </Button>
              </Box>

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "40px",
                  borderColor: "#e5e7eb",
                  alignSelf: "center",
                }}
              />

              {/* Wrap Text Button */}
              <Button
                variant={!wrapText ? "contained" : "outlined"}
                onClick={() => setWrapText(!wrapText)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.75,
                  fontSize: "13px",
                  fontWeight: 500,
                  ...(!wrapText
                    ? {
                        backgroundColor: "#4B68D4",
                        "&:hover": {
                          backgroundColor: "#3A54B6",
                        },
                      }
                    : {
                        borderColor: "#d0d5dd",
                        color: "#667085",
                        "&:hover": {
                          borderColor: "#4B68D4",
                          backgroundColor: "#f5f7fa",
                        },
                      }),
                }}
              >
                Unwrap Text
              </Button>

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "40px",
                  borderColor: "#e5e7eb",
                  alignSelf: "center",
                }}
              />

              {/* Section Display Dropdown */}
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="section-display-label">
                  Section Display
                </InputLabel>
                <Select
                  labelId="section-display-label"
                  id="section-display"
                  multiple
                  value={Object.keys(visibleGroups).filter(
                    (key) => visibleGroups[key]
                  )}
                  onChange={(event) => {
                    const selectedGroups = event.target.value;
                    const newVisibleGroups = {};
                    Object.keys(visibleGroups).forEach((groupId) => {
                      newVisibleGroups[groupId] =
                        selectedGroups.includes(groupId);
                    });
                    setVisibleGroups(newVisibleGroups);
                  }}
                  input={<OutlinedInput label="Section Display" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "None";
                    if (selected.length === Object.keys(visibleGroups).length)
                      return "All Sections";
                    return `${selected.length} section${
                      selected.length > 1 ? "s" : ""
                    }`;
                  }}
                  sx={{
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4B68D4",
                    },
                  }}
                >
                  {columnGroupButtons.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={visibleGroups[group.id]} />
                      <ListItemText primary={group.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Show All Button */}
              <Button
                variant="outlined"
                onClick={() => {
                  const allVisible = {};
                  Object.keys(visibleGroups).forEach((key) => {
                    allVisible[key] = true;
                  });
                  setVisibleGroups(allVisible);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.75,
                  fontSize: "13px",
                  fontWeight: 500,
                  borderColor: "#d0d5dd",
                  color: "#667085",
                  "&:hover": {
                    borderColor: "#4B68D4",
                    backgroundColor: "#f5f7fa",
                  },
                }}
              >
                Show All
              </Button>
            </Box>
          </Box>
        )}

        {/* Quick Navigation - Collapsible */}
        {headerExpanded && (
          <Box
            sx={{
              mb: 1,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: "13px",
                color: "#667085",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Quick Navigation
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {columnGroupButtons.map((group) => (
                <Button
                  key={group.id}
                  variant="outlined"
                  size="small"
                  onClick={() => scrollToColumnGroup(group.id)}
                  disabled={!visibleGroups[group.id]}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    fontSize: "12px",
                    fontWeight: 500,
                    borderColor: "#d0d5dd",
                    color: "#667085",
                    "&:hover": {
                      borderColor: "#4B68D4",
                      backgroundColor: "#EEF2FF",
                      color: "#4B68D4",
                    },
                    "&:disabled": {
                      borderColor: "#e5e7eb",
                      color: "#d0d5dd",
                    },
                  }}
                >
                  {group.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Data Grid - Takes up remaining space */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 2,
          pb: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DataGridPremium
          rows={items}
          columns={columns}
          columnGroupingModel={visibleColumnGroupingModel}
          apiRef={apiRef}
          processRowUpdate={processRowUpdate}
          getRowClassName={getRowClassName}
          getCellClassName={getCellClassName}
          loading={loading}
          disableRowSelectionOnClick
          experimentalFeatures={{ columnGrouping: true }}
          pinnedColumns={{ left: frozenColumns }}
          // PERFORMANCE OPTIMIZATION: Fixed row height instead of 'auto'
          getRowHeight={() => rowHeight}
          autoHeight={false}
          hideFooter={false}
          // PERFORMANCE OPTIMIZATION: Buffer settings for virtualization
          rowBuffer={5}
          columnBuffer={2}
          // Client-side filtering
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          filterMode="client"
          slots={{
            footer: () => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Rows: {items.length}
                </Typography>
                {lastSavedTime && (
                  <Typography variant="body2" color="text.secondary">
                    | Last saved: {lastSavedTime.toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            ),
          }}
          sx={{
            height: "100%",
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              fontWeight: 600,
              borderBottom: "2px solid #e5e7eb",
              minHeight: "140px !important",
              maxHeight: "140px !important",
              zIndex: 3,
              position: "sticky",
              top: 0,
              backgroundColor: "white",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              minHeight: "140px !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              zIndex: 1,
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              minHeight: "60px !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: `${rowHeight}px !important`,
              maxHeight: wrapText
                ? "none !important"
                : `${rowHeight}px !important`,
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f9fafb",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f3f4f6",
              fontSize: "13px",
              whiteSpace: "normal",
              lineHeight: "1.5",
              wordWrap: "break-word",
              display: "flex",
              alignItems: "center",
              padding: "8px",
            },
            // PERFORMANCE OPTIMIZATION: Use pre-calculated column group styles
            ...columnGroupStyles,
            "& .MuiDataGrid-columnHeader": {
              minHeight: "70px !important",
              maxHeight: "70px !important",
              height: "70px !important",
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                whiteSpace: "normal",
                lineHeight: "1.3",
                textAlign: "center",
                wordWrap: "break-word",
                padding: "4px",
              },
            },
            "& .MuiDataGrid-columnHeaderRow": {
              minHeight: "70px !important",
              maxHeight: "70px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "12px",
              color: "#374151",
              ...(wrapText && {
                whiteSpace: "normal",
                lineHeight: "1.3",
                wordWrap: "break-word",
              }),
            },
            // PERFORMANCE OPTIMIZATION: Use pre-calculated styles
            ...reqColumnHeaderStyles,
            ...approvalColumnHeaderStyles,
            ...actualColumnHeaderStyles,
            ...reqCellStyles,
            ...approvalCellStyles,
            ...actualCellStyles,
            ...pastDueCellStyles,
            [`& .${gridClasses.row}.row--edited`]: {
              backgroundColor: (theme) => {
                if (theme.palette.mode === "light") {
                  return "rgba(255, 254, 176, 0.3)";
                }
                return darken("rgba(255, 254, 176, 1)", 0.6);
              },
            },
            "& .cell--past-due": {
              backgroundColor: "#FEE2E2 !important",
              borderLeft: "3px solid #DC2626 !important",
              fontWeight: 600,
              color: "#991B1B",
            },
          }}
        />
      </Box>

      {/* Comment Edit Modal */}
      <WIPCommentModal
        open={commentModalOpen}
        onClose={handleCloseCommentModal}
        onSave={handleSaveComments}
        rowData={commentModalData.row}
        fieldName={commentModalData.field}
        approvalInfo={commentModalData.approvalInfo}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        readOnly={readOnly}
      />

      {/* Bulk Update Modal */}
      <Dialog
        open={bulkUpdateModalOpen}
        onClose={() => setBulkUpdateModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bulk Update:{" "}
          {(bulkUpdateField &&
            wipColumnsRef.current.find((c) => c.field === bulkUpdateField)
              ?.headerName) ||
            bulkUpdateField}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {bulkUpdateField &&
              (() => {
                const column = wipColumnsRef.current.find(
                  (c) => c.field === bulkUpdateField
                );
                const isDateColumn = column?.type === "date";
                const isNumberColumn = column?.type === "number";
                const isStatusColumn =
                  column?.type === "singleSelect" && column?.valueOptions;
                const isCommentField = bulkUpdateField.includes("(COMMENT)");

                if (isDateColumn) {
                  return (
                    <TextField
                      fullWidth
                      label="Date Value"
                      type="date"
                      value={
                        bulkUpdateDateValue
                          ? bulkUpdateDateValue.toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) => {
                        const dateValue = e.target.value
                          ? new Date(e.target.value + "T00:00:00")
                          : null;
                        setBulkUpdateDateValue(dateValue);
                      }}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  );
                } else if (isStatusColumn) {
                  return (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Status Value</InputLabel>
                      <Select
                        value={bulkUpdateValue}
                        onChange={(e) => setBulkUpdateValue(e.target.value)}
                        label="Status Value"
                      >
                        {column.valueOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label || option.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                } else if (isCommentField) {
                  return (
                    <TextField
                      fullWidth
                      label="Comment (will be added to existing comments)"
                      value={bulkUpdateValue}
                      onChange={(e) => setBulkUpdateValue(e.target.value)}
                      variant="outlined"
                      multiline
                      rows={4}
                      helperText="This comment will be appended to any existing comments for the selected cells."
                    />
                  );
                } else if (isNumberColumn) {
                  return (
                    <TextField
                      fullWidth
                      label="Number Value"
                      type="number"
                      value={bulkUpdateValue}
                      onChange={(e) => setBulkUpdateValue(e.target.value)}
                      variant="outlined"
                    />
                  );
                } else {
                  return (
                    <TextField
                      fullWidth
                      label="Text Value"
                      value={bulkUpdateValue}
                      onChange={(e) => setBulkUpdateValue(e.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                    />
                  );
                }
              })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateModalOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (!bulkUpdateField) return;

              const column = wipColumnsRef.current.find(
                (c) => c.field === bulkUpdateField
              );
              let valueToUse = "";

              if (column?.type === "date") {
                if (bulkUpdateDateValue) {
                  // Convert Date to ISO string for the update
                  valueToUse = bulkUpdateDateValue.toISOString();
                } else {
                  // Empty date - clear the field
                  valueToUse = "";
                }
              } else {
                valueToUse = bulkUpdateValue;
              }

              if (valueToUse !== "") {
                handleBulkUpdate(bulkUpdateField, valueToUse);
              }

              setBulkUpdateModalOpen(false);
              setBulkUpdateField(null);
              setBulkUpdateValue("");
              setBulkUpdateDateValue(null);
            }}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          icon={
            snackbarSeverity === "success" ? <CheckCircleIcon /> : <ErrorIcon />
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WIPBatchList;
